import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarDays, Landmark, Users, BadgeEuro, Car, ArrowRight } from "lucide-react";
import { useSite } from "@/i18n/SiteContext";
import { api, waLink } from "@/lib/api";
import { SectionHeading } from "@/components/site/SectionHeading";
import { TourCard } from "@/components/site/TourCard";
import { QuickRequestForm } from "@/components/site/QuickRequestForm";
import { Reveal } from "@/components/site/Reveal";
import { IMAGES } from "@/lib/images";
import { DestinationsGrid } from "@/components/site/DestinationsGrid";
import { PackageCard } from "@/components/site/PackageCard";
import { ReviewsSection } from "@/components/site/ReviewsSection";

export default function HomePage() {
  const { t, settings } = useSite();
  const reduce = useReducedMotion();
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    api.get("/tours").then((r) => setTours(r.data)).finally(() => setLoading(false));
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => setPackages([]));
  }, []);

  const featured = [...tours.filter((x) => x.featured), ...tours.filter((x) => !x.featured)].slice(0, 6);
  const cats = ["archaeology", "cities", "food", "nature", "coast"].filter((c) => tours.some((x) => x.category === c));

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative h-screen min-h-[680px] flex items-end overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <motion.img initial={reduce ? false : { scale: 1.08 }} animate={{ scale: 1 }} transition={{ duration: 2.4, ease: "easeOut" }} src={IMAGES.hero} alt="The Forum of Pompeii with Vesuvius behind" className="w-full h-full object-cover" />
          <div className="hero-overlay absolute inset-0" />
          <div className="absolute inset-0 bg-gradient-to-r from-ocean/75 via-ocean/35 to-transparent" />
        </div>
        <div className="relative z-10 w-full max-w-6xl mx-auto px-6 pb-24 md:pb-28">
          <motion.div initial={reduce ? false : { opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.2 }} className="max-w-3xl">
            <p className="text-[11px] md:text-xs font-medium tracking-[0.4em] text-ivory/90 uppercase mb-7" data-testid="hero-tagline">{t("hero.eyebrow")}</p>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-[88px] text-ivory font-normal leading-[1.02] tracking-[-0.02em]">
              {t("hero.title1")}<br /><span className="italic">{t("hero.title2")}</span>
            </h1>
            <p className="mt-7 text-base md:text-lg text-ivory/90 max-w-xl leading-relaxed font-light">{t("hero.sub")}</p>
          </motion.div>
          <motion.div initial={reduce ? false : { opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.5 }} className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Link to="/tours" data-testid="hero-explore-cta" className="inline-flex items-center gap-3 px-8 py-4 border border-ivory/70 text-ivory text-xs font-medium tracking-[0.25em] uppercase hover:bg-ivory hover:text-ocean btn-lift">{t("hero.explore")} <ArrowRight size={15} /></Link>
            <a href="#plan" data-testid="hero-plan-cta" className="px-8 py-4 rounded-full bg-ivory text-ocean text-xs font-medium tracking-[0.25em] uppercase hover:bg-sand-brand btn-lift">{t("hero.plan")}</a>
          </motion.div>
        </div>
      </section>

      {/* INTRO */}
      <section className="section-padding" data-testid="intro-section">
        <SectionHeading eyebrow={t("intro.eyebrow")} title={t("intro.title")} subtitle={t("intro.sub")} />
        <Reveal className="mt-12 flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
          {cats.map((c) => (
            <Link key={c} to={`/tours?category=${c}`} data-testid={`home-category-${c}`} className="px-5 py-2.5 rounded-full bg-white border border-sand text-sm text-ocean hover:border-sea hover:text-sea shadow-soft btn-lift">
              {t(`cat.${c}`)}
            </Link>
          ))}
        </Reveal>
        <div className="mt-16 max-w-5xl mx-auto grid grid-cols-3 gap-3 md:gap-6 items-end" data-testid="intro-moments">
          {[["food", "Pasta with seafood", "md:translate-y-6"], ["culture", "Ancient amphorae", ""], ["coast", "Flowered steps in Positano", "md:translate-y-6"]].map(([k, alt, off], i) => (
            <Reveal key={k} delay={i * 0.08} className={off}>
              <div className="img-zoom aspect-[4/5] rounded-2xl overflow-hidden shadow-soft bg-sand">
                <img src={IMAGES.moments[k]} alt={alt} loading="lazy" className="w-full h-full object-cover" />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* DESTINATIONS */}
      <section className="pb-24 md:pb-32 px-6" data-testid="destinations-section">
        <div className="max-w-6xl mx-auto">
          <SectionHeading eyebrow={t("dest.eyebrow")} title={t("dest.title")} subtitle={t("dest.sub")} />
          <div className="mt-14"><DestinationsGrid tours={tours} /></div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="pb-24 md:pb-32 px-6" data-testid="featured-section">
        <div className="max-w-6xl mx-auto">
          <SectionHeading eyebrow={t("featured.eyebrow")} title={t("featured.title")} subtitle={t("featured.sub")} />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
            {loading
              ? [0, 1, 2].map((i) => <div key={i} className="aspect-[4/3] rounded-2xl bg-sand/60 animate-pulse" />)
              : featured.map((tour, i) => <TourCard key={tour.id} tour={tour} index={i} />)}
          </div>
          <div className="mt-14 text-center">
            <Link to="/tours" data-testid="view-all-tours" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border border-ocean text-ocean text-xs font-medium tracking-[0.2em] uppercase hover:bg-ocean hover:text-ivory btn-lift">
              {t("featured.viewAll")}
            </Link>
          </div>
        </div>
      </section>

      {/* PACKAGES */}
      {packages.length > 0 && (
        <section className="section-padding bg-sand-brand/25 border-y border-sand/60" data-testid="packages-section">
          <div className="max-w-6xl mx-auto">
            <SectionHeading eyebrow={t("pkg.eyebrow")} title={t("pkg.title")} subtitle={t("pkg.sub")} />
            <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {packages.slice(0, 3).map((p, i) => <PackageCard key={p.id} pkg={p} index={i} />)}
            </div>
            <div className="mt-12 text-center">
              <Link to="/packages" data-testid="view-all-packages" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border border-ocean text-ocean text-xs font-medium tracking-[0.2em] uppercase hover:bg-ocean hover:text-ivory btn-lift">
                {t("pkg.all")} <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* HOW IT WORKS */}
      <section className="section-padding bg-white" data-testid="how-section">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <Reveal className="lg:col-span-5" y={24}>
            <div className="img-zoom aspect-[4/5] rounded-3xl overflow-hidden shadow-soft-lg">
              <img src={IMAGES.gods} alt="The ruins of Herculaneum" loading="lazy" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <div className="lg:col-span-7">
            <SectionHeading align="left" eyebrow={t("how.eyebrow")} title={t("how.title")} subtitle={t("how.sub")} />
            <ol className="mt-10 space-y-8" data-testid="how-steps">
              {[1, 2, 3].map((k, idx) => (
                <Reveal key={k} delay={idx * 0.08}>
                  <li className="flex gap-6" data-testid={`how-step-${k}`}>
                    <span className="w-12 h-12 shrink-0 rounded-full bg-sage-mist border border-sage-light flex items-center justify-center font-serif text-lg text-sage-dark">{k}</span>
                    <div>
                      <h3 className="font-serif text-xl md:text-2xl text-ocean">{t(`how.s${k}t`)}</h3>
                      <p className="mt-2 text-sm md:text-base text-ocean/65 leading-relaxed">{t(`how.s${k}d`)}</p>
                    </div>
                  </li>
                </Reveal>
              ))}
            </ol>
            <Link to="/tours" data-testid="how-cta" className="inline-flex items-center gap-2 mt-10 px-8 py-3.5 bg-ocean text-ivory text-sm font-medium tracking-wider uppercase rounded-full hover:bg-ocean-700 btn-lift">
              {t("how.cta")} <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="bg-ocean section-padding relative noise" data-testid="philosophy-section">
        <div className="max-w-6xl mx-auto relative z-10">
          <SectionHeading dark eyebrow={t("philosophy.eyebrow")} title={t("philosophy.title")} subtitle={t("philosophy.sub")} />
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
            {[
              { i: Landmark, ti: t("philosophy.i1t"), d: t("philosophy.i1d") },
              { i: Users, ti: t("philosophy.i2t"), d: t("philosophy.i2d") },
              { i: BadgeEuro, ti: t("philosophy.i3t"), d: t("philosophy.i3d") },
              { i: Car, ti: t("extra.i4t"), d: t("extra.i4d") },
            ].map(({ i: Icon, ti, d }, idx) => (
              <Reveal key={idx} delay={idx * 0.08} className="text-center">
                <div className="w-14 h-14 mx-auto rounded-full bg-ivory/5 border border-ivory/10 flex items-center justify-center mb-5"><Icon size={24} className="text-sand-brand" /></div>
                <h3 className="font-serif text-xl text-ivory mb-3">{ti}</h3>
                <p className="text-sm text-ivory/65 leading-relaxed max-w-xs mx-auto">{d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <ReviewsSection tours={tours} />

      {/* PLAN */}
      <section id="plan" className="section-padding bg-ivory scroll-mt-20" data-testid="plan-section">
        <div className="max-w-4xl mx-auto">
          <Reveal className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-sea/15 mb-6"><CalendarDays size={30} className="text-sea" /></div>
            <h2 className="font-serif text-3xl md:text-4xl text-ocean mb-4">{t("dates.title")}</h2>
            <p className="text-base md:text-lg text-ocean/60 leading-relaxed max-w-2xl mx-auto">{t("dates.sub")}</p>
          </Reveal>
          <Reveal className="bg-white rounded-3xl shadow-soft-xl p-8 md:p-12"><QuickRequestForm /></Reveal>
        </div>
      </section>

      {/* TAILOR MADE */}
      <section className="relative overflow-hidden" data-testid="tailor-section">
        <div className="absolute inset-0">
          <img src={IMAGES.tailor} alt="Royal Palace of Caserta" className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-ocean/80" />
        </div>
        <div className="relative section-padding">
          <Reveal className="max-w-3xl mx-auto text-center">
            <p className="text-xs font-medium tracking-super-wide text-sand-brand uppercase mb-4">{t("tailor.eyebrow")}</p>
            <h2 className="font-serif text-3xl md:text-5xl text-ivory font-normal leading-tight">{t("tailor.title")}</h2>
            <p className="mt-5 text-base md:text-lg font-light text-ivory/80 leading-relaxed">{t("tailor.text")}</p>
            <div className="w-14 h-px bg-sand-brand mx-auto mt-6" />
            <Link to="/contact" data-testid="tailor-made-cta" className="inline-block mt-10 px-10 py-4 rounded-full bg-ivory text-ocean font-medium text-xs tracking-[0.2em] uppercase hover:bg-sand-brand btn-lift">{t("tailor.cta")}</Link>
          </Reveal>
        </div>
      </section>

      {/* TRUST */}
      <section className="section-padding" data-testid="trust-section">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16 text-center">
          {[1, 2, 3].map((k, idx) => (
            <Reveal key={k} delay={idx * 0.1}>
              <p className="font-serif text-4xl md:text-5xl text-ocean mb-2" data-testid={`stat-${k}`}>{t(`trust.s${k}`)}</p>
              <p className="text-sm font-medium tracking-wider uppercase text-sea mb-3">{t(`trust.l${k}`)}</p>
              <p className="text-sm text-ocean/60 leading-relaxed">{t(`trust.d${k}`)}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* LIFESTYLE STRIP */}
      <section data-testid="lifestyle-strip">
        <div className="lifestyle-strip">
          {IMAGES.strip.map((im, i) => (
            <Reveal key={im.src} delay={i * 0.06} y={0} className="img-zoom aspect-square">
              <img src={im.src} alt={im.alt} loading="lazy" className="w-full h-full object-cover" />
            </Reveal>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="section-padding bg-ivory" data-testid="final-cta-section">
        <Reveal className="max-w-3xl mx-auto text-center">
          <p className="text-xs font-medium tracking-super-wide text-sea uppercase mb-4">{t("final.eyebrow")}</p>
          <h2 className="font-serif text-3xl md:text-4xl text-ocean font-normal">{t("final.title")}</h2>
          <p className="mt-4 text-base text-ocean/60 leading-relaxed">{t("final.text")}</p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/tours" data-testid="final-plan-cta" className="px-8 py-3.5 rounded-full bg-ocean text-ivory font-medium text-xs tracking-[0.2em] uppercase hover:bg-ocean-700 btn-lift">{t("final.plan")}</Link>
            {settings.whatsapp && (
              <a href={waLink(settings.whatsapp, t("wa.default"))} target="_blank" rel="noopener noreferrer" data-testid="final-whatsapp-cta" className="px-8 py-3.5 rounded-full border border-ocean text-ocean font-medium text-xs tracking-[0.2em] uppercase hover:bg-ocean hover:text-ivory btn-lift">{t("final.wa")}</a>
            )}
          </div>
        </Reveal>
      </section>
    </div>
  );
}
